import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CalendarClock, Loader2, Eye, EyeOff } from "lucide-react";
import { saveUserTokenSession } from "@/services/authServices";
import { AuthenticationService } from "@/api/auth";
import { ApiError } from "@/api/auth";
import {
  getLambdaResponseMessage,
  isLambdaErrorResponse,
  normalizeLambdaResponse,
} from "@/utils/lambdaResponse";
import Logo from "@/assets/img/eventLogo1.svg";

const getAuthErrorMessage = (error: unknown) => {
  if (error instanceof ApiError) {
    const normalized = normalizeLambdaResponse(error.body);
    if (isLambdaErrorResponse(normalized)) {
      return getLambdaResponseMessage(normalized);
    }

    return error.body?.message || error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "There was a problem signing you in.";
};

const SignIn = () => {
  const { postAuthLogin } = AuthenticationService;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);

  // Get the return URL from location state or default to dashboard
  const from = location.state?.from?.pathname || "/dashboard";

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Simulating an API call
      // await new Promise(resolve => setTimeout(resolve, 1000));

      // Mock validation logic
      if (email && password) {
        // Success - Store the token with consistent naming
        // localStorage.setItem('authToken', 'mock-jwt-token');
        // localStorage.setItem('user', JSON.stringify({ email }));
        const rawResponse: any = await postAuthLogin({ email, password });
        const response = normalizeLambdaResponse(rawResponse);

        if (isLambdaErrorResponse(response)) {
          toast({
            variant: "destructive",
            title: "Authentication failed",
            description: getLambdaResponseMessage(response),
          });
          return;
        }

        if (
          (response.statusCode === 200 || response.statusCode === 201) &&
          response.body.data.id_token
        ) {
          toast({
            title: "Sign in successful",
            description:
              response.body.message || "Welcome back to CecureStream Events!",
          });
          await saveUserTokenSession(response);
          // Navigate to the page the user was trying to access, or dashboard
          navigate(from);
        } else if (
          (response.statusCode === 200 || response.statusCode === 201) &&
          response.body.data.challenge === "NEW_PASSWORD_REQUIRED"
        ) {
          // If the user needs to set a new password, redirect to the change password page
          navigate(
            `/reset-password?token=${response.body.data.session}&email=${response.body.data.email}`
          );
          // Show a toast message indicating the need for a new password
          toast({
            title: "New password required",
            description: "Please set a new password to continue.",
          });
        } else {
          toast({
            variant: "destructive",
            title: "Error signing in",
            description:
              response.body.message ||
              "Please check your credentials and try again.",
          });
        }
      } else {
        toast({
          variant: "destructive",
          title: "Error signing in",
          description: "Please check your credentials and try again.",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Authentication failed",
        description: getAuthErrorMessage(error),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="flex items-center mb-4">
        {/* <CalendarClock className="h-8 w-8 text-cecure-700 mr-2" />
        <span className="text-2xl font-bold text-cecure-900 font-heading">
          CecureStream <span className="text-accent">Events</span>
        </span> */}
        <img
          src={Logo}
          alt="CecureStreams Logo"
          className="h-full w-[200px] text-cecure-700"
        />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-2xl">Sign In</CardTitle>
          <CardDescription className="text-center">
            Enter your credentials to access your account
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSignIn}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-cecure-700 hover:text-cecure-900"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute top-1/2 right-3 transform -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <Eye className="h-5 w-5 text-cecure-700" />
                  ) : (
                    <EyeOff className="h-5 w-5 text-cecure-700" />
                  )}
                </button>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full bg-cecure-700 hover:bg-cecure-800"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
            <div className="text-center text-sm">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="text-cecure-700 hover:text-cecure-900 font-medium"
              >
                Sign Up
              </Link>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default SignIn;
